import { useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

interface IOptions {
  additions: Record<string, string>;
  deletions?: Array<string>;
}

const getModifiedParams = (
  params: URLSearchParams,
  additions: Record<string, string>,
  deletions?: Array<string>
) => {
  const newParams = new URLSearchParams(params);
  deletions?.forEach((deletionKey) => {
    newParams.delete(deletionKey);
  });
  Object.keys(additions).forEach((additionKey) => {
    const additionValue = additions[additionKey];
    newParams.set(additionKey, additionValue);
  });
  return newParams;
};

const useModalRoute = ({ additions, deletions }: IOptions) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();

  const forceClose = useRef(false);

  const onClose = async () => {
    const newParams = getModifiedParams(searchParams, {}, [
      ...(deletions ?? []),
      ...Object.keys(additions),
    ]);
    setSearchParams(newParams, {
      replace: true,
    });
  };

  const modalParams = getModifiedParams(
    searchParams,
    additions,
    deletions ?? []
  );

  const modalRoute = `${pathname}?${modalParams.toString()}`;

  /**
   * function for use cases where dynamically generated modal routes are required
   * @param newAdditions
   * @param newDeletions
   * @returns modal search params
   */
  const getModalParams = (
    newAdditions: Record<string, string> = {},
    newDeletions?: Array<string>
  ) => {
    const newParams = getModifiedParams(
      searchParams,
      { ...additions, ...newAdditions },
      [...(deletions ?? []), ...(newDeletions ?? [])]
    );
    return newParams;
  };

  /**
   * function for use cases where dynamically generated modal routes are required
   * @param newAdditions
   * @param newDeletions
   * @returns url with updated params
   */
  const getModalRoute = (
    newAdditions: Record<string, string> = {},
    newDeletions?: Array<string>
  ) => {
    return `${pathname}?${getModalParams(
      newAdditions,
      newDeletions
    ).toString()}`;
  };

  const isOpen = useMemo(() => {
    const allParamsValid = Object.keys(additions ?? {}).every((key) =>
      searchParams.has(key)
    );
    return allParamsValid;
  }, [additions, searchParams]);

  const onOpen = () => {
    setSearchParams(modalParams, { replace: true });
  };

  const onOpenChange = (open?: boolean) => {
    if (open) {
      onOpen();
    } else {
      onClose();
    }
  };

  const onToggle = () => {
    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  };

  return {
    isOpen,
    onClose,
    modalRoute,
    modalParams,
    getModalRoute,
    getModalParams,
    onOpen,
    onOpenChange,
    onToggle,
    forceClose,
  };
};

export default useModalRoute;
